import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Heading from '../../../generic-components/Heading';
import Icon from '../../../generic-components/Icon';
import expressIcon from '../../../assets/svg/Express.svg';
import standardIcon from '../../../assets/svg/Standard.svg';
import checkMarkIcon from '../../../assets/svg/whiteCheck.svg';
import ReviewItemWrapper from './ReviewItemWrapper';
import Button from '../../../generic-components/Button';
import { AchOutContext } from '../pages';
import { convertValueToUSD } from '../../../helpers/currency.helper';
import { REVIEW_DATA } from '../constants';
import TooltipWithTriangle from '../../../generic-components/TooltipWithTriangle';
import { trackMixPanel } from '../../../services/mixpanel.service';
import { useStore } from '../../../store';
import { useAchOutFeesQuery } from '../../../hooks/query/useAchOutFeesQuery';
import { ReactComponent as IconToolTipQuestion } from '../../../assets/svg/shared/icon-tooltip-question.svg';

const TRANSFER_TYPES = {
  EXPRESS: 'Express',
  STANDARD: 'Standard',
};

interface IFeeItem {
  achTransferType: string;
  arrivalDate: string;
  arrivalDateShort: string;
  fee: number;
}

export const defaultFeeItem = {
  achTransferType: '',
  arrivalDate: '',
  arrivalDateShort: '',
  fee: 0,
};

interface IProps {
  onSubmit: () => void;
  setEditMode: (type: string) => void;
}

const { STYLES } = REVIEW_DATA;

const ReviewAchOut = ({ onSubmit, setEditMode }: IProps) => {
  const FlowContext = useContext(AchOutContext);
  const { isLoading } = FlowContext;
  const [transferType, setTransferType] = useState<string>('');
  const [isToolTipShown, setIsToolTipShown] = useState<boolean>(false);
  const { t } = useTranslation();
  const {
    user: { customerId },
    getPersonalAccount,
  } = useStore((state) => state);
  const getBankTransferFormData = useStore((state) => state.getBankTransferFormData);
  const contextType = useStore((state) => state.contextType);
  const { businessName, transferAmount, accountNumber, routingNumber } = getBankTransferFormData();
  const setChosenAchFee = useStore((state) => state.setChosenAchFee);
  const chosenAchFee = useStore((state) => state.chosenAchFee);
  const { accountExternalId } = getPersonalAccount();
  const { refetch: getAchOutFees, data: achFeesResponse } = useAchOutFeesQuery({
    customerId,
    accountExternalId,
    amount: transferAmount,
    onSuccess: () => {},
  });

  const shouldShowEditButtons = useMemo(() => {
    const restrictedContextTypes = ['BILL_PAY'];
    return !restrictedContextTypes.includes(contextType);
  }, [contextType]);

  const expressTransferData = useMemo(() => {
    const { fees } = achFeesResponse || {};
    const data = fees?.find((feeItem: IFeeItem) => feeItem?.achTransferType === 'EXPRESS') || null;
    return data;
  }, [achFeesResponse]);

  const standardTransferData = useMemo(() => {
    const { fees } = achFeesResponse || {};
    const data: IFeeItem | null = fees?.find((feeItem: IFeeItem) => feeItem?.achTransferType === 'STANDARD') || null;
    return data;
  }, [achFeesResponse]);

  const transferFee = useMemo(() => {
    if (transferType === TRANSFER_TYPES.STANDARD && standardTransferData) {
      setChosenAchFee(standardTransferData);
      return t('ACH_OUT_REVIEW.FREE_FEE');
    }
    if (transferType === TRANSFER_TYPES.EXPRESS && expressTransferData) {
      setChosenAchFee(expressTransferData);
      return convertValueToUSD(expressTransferData.fee, 2);
    }
    return t('ACH_OUT_NEW_REVIEW.NO_FEE');
  }, [transferType, achFeesResponse]);
  useEffect(() => {
    trackMixPanel('View ACH to 3rd Party - Review Screen');
    getAchOutFees();
  }, []);

  return (
    <div className='flex flex-col items-center justify-center w-full h-full'>
      <div className='w-full flex flex-col items-center h-full justify-center grow'>
        <div className={STYLES.CONTAINER}>
          <Heading title={t('ACH_OUT_REVIEW.TITLE')} className={STYLES.MAIN_HEAD} />
          <div className={STYLES.WRAPPER_CONTAINER}>
            <div className={STYLES.REVIEW_ITEM_CONTAINER}>
              <Heading
                title={t('ACH_OUT_REVIEW.REVIEW_ITEM_TITLES.TRANSFER_AMOUNT')}
                className={STYLES.REVIEW_ITEM_MAIN_HEADER}
              />
              {shouldShowEditButtons && (
                <Button
                  className={STYLES.EDIT}
                  onClick={() => setEditMode('AMOUNT')}
                  id='ConfirmReviewEditCTA'
                  title={t('ACH_OUT_REVIEW.REVIEW_ITEM_TITLES.EDIT')}
                />
              )}
            </div>
            <div className={STYLES.REVIEW_ITEM_CONTAINER}>
              <Heading title={t('ACH_OUT_REVIEW.REVIEW_ITEM_TITLES.AMOUNT')} className={STYLES.REVIEW_ITEM_HEADER} />
              <Heading
                testId='amountToReview'
                title={convertValueToUSD(Number(transferAmount), 2)}
                className={STYLES.REVIEW_AMOUNT}
              />
            </div>
            <div className={STYLES.UNDERLINE} />
            <div className={STYLES.REVIEW_ITEM_CONTAINER}>
              <Heading
                title={t('ACH_OUT_REVIEW.REVIEW_ITEM_TITLES.PAYEE_DETAILS')}
                className={STYLES.REVIEW_ITEM_MAIN_HEADER}
              />
              {shouldShowEditButtons && (
                <Button
                  className={STYLES.EDIT}
                  onClick={() => setEditMode('DETAILS')}
                  id='ConfirmReviewEditPayeeCTA'
                  title={t('ACH_OUT_REVIEW.REVIEW_ITEM_TITLES.EDIT')}
                />
              )}
            </div>
            <div className={STYLES.REVIEW_ITEM_CONTAINER}>
              <Heading title={t('ACH_OUT_REVIEW.REVIEW_ITEM_TITLES.SEND_TO')} className={STYLES.REVIEW_ITEM_HEADER} />
              <Heading testId='payeeToReview' title={businessName} className={STYLES.REVIEW_PAYEE} />
            </div>
            <div className={STYLES.REVIEW_ITEM_CONTAINER}>
              <Heading
                title={t('ACH_OUT_REVIEW.REVIEW_ITEM_TITLES.ACCOUNT_NUMBER')}
                className={STYLES.REVIEW_ITEM_HEADER}
              />
              <Heading testId='accNumberToReview' title={accountNumber} className={STYLES.REVIEW_ACCOUNT_NUMBER} />
            </div>
            <div className={STYLES.REVIEW_ITEM_CONTAINER}>
              <Heading
                title={t('ACH_OUT_REVIEW.REVIEW_ITEM_TITLES.ROUTING_NUMBER')}
                className={STYLES.REVIEW_ITEM_HEADER}
              />
              <Heading testId='routNumberReview' title={routingNumber} className={STYLES.REVIEW_ACCOUNT_NUMBER} />
            </div>
          </div>
          <ReviewItemWrapper>
            <div className={STYLES.TRANSFER_ITEMS_CONTAINER}>
              <Heading title={t('ACH_OUT_REVIEW.TRANSFER.TITLE')} className={STYLES.TRANSFER_ITEMS_HEAD} />
              <div
                role='button'
                onClick={() => setTransferType(TRANSFER_TYPES.EXPRESS)}
                onKeyDown={() => {}}
                tabIndex={0}
                id='transferTypeExpress'
                className={`flex items-center py-[12px] bg-bw01 hover:border-action03 hover:bg-bw02 px-[16px] border mx-[16px] ${transferType === TRANSFER_TYPES.EXPRESS ? 'border-action03' : 'border-bw03'} rounded-[8px] mb-[12px]`}
              >
                <div
                  className={`relative rounded-[50%] w-[20px] h-[20px] ${transferType === TRANSFER_TYPES.EXPRESS ? 'bg-action03' : 'bg-bw02 border border-bw03'} mr-[18px]`}
                >
                  {transferType === TRANSFER_TYPES.EXPRESS ? (
                    <Icon
                      src={checkMarkIcon}
                      className='w-[70%] absolute left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%]'
                    />
                  ) : null}
                </div>
                <div className='flex flex-col'>
                  <div className='flex items-center mb-[4px]'>
                    <Icon src={expressIcon} className={STYLES.TRANSFER_ICON} />
                    <Heading title={TRANSFER_TYPES.EXPRESS} className='text-base text-action03 font-metricMedium' />
                  </div>
                  {expressTransferData && (
                    <Heading title={expressTransferData.arrivalDate} className='text-[14px] text-bw07 font-metric' />
                  )}
                </div>
              </div>
              <div
                role='button'
                onClick={() => setTransferType(TRANSFER_TYPES.STANDARD)}
                onKeyDown={() => {}}
                tabIndex={0}
                id='transferTypeStandard'
                className={`flex items-center py-[12px] px-[16px] bg-bw01 hover:border-action03 hover:bg-bw02 border mx-[16px] ${transferType === TRANSFER_TYPES.STANDARD ? 'border-action03' : 'border-bw03'} rounded-[8px] mb-[12px]`}
              >
                <div
                  className={`relative rounded-[50%] w-[20px] h-[20px] ${transferType === TRANSFER_TYPES.STANDARD ? 'bg-action03' : 'bg-bw02 border border-bw03'} mr-[18px]`}
                >
                  {transferType === TRANSFER_TYPES.STANDARD ? (
                    <Icon
                      src={checkMarkIcon}
                      className='w-[70%] absolute left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%]'
                    />
                  ) : null}
                </div>
                <div className='flex flex-col'>
                  <div className='flex items-center mb-[4px]'>
                    <Icon src={standardIcon} className={STYLES.TRANSFER_ICON} />
                    <Heading title={TRANSFER_TYPES.STANDARD} className='text-base text-action03 font-metricR' />
                  </div>
                  {standardTransferData && (
                    <Heading title={standardTransferData?.arrivalDate} className='text-[14px] text-bw07 font-metricR' />
                  )}
                </div>
              </div>
              <div className='flex items-center px-[16px] border-t-[1px] border-bw03 h-[54px]'>
                <Heading
                  title={t('ACH_OUT_REVIEW.TRANSFER.FEE')}
                  className='mr-[4px] font-metricR text-bw07 text-[14px]'
                />
                <div
                  onMouseEnter={() => setIsToolTipShown(true)}
                  onMouseLeave={() => {
                    setIsToolTipShown(false);
                  }}
                  className='relative'
                >
                  {isToolTipShown && (
                    <TooltipWithTriangle
                      tooltipContainerClassName={STYLES.TOOL_TIP_CONTAINER}
                      tooltipContentClassName={STYLES.TOOL_TIP_CONTENT}
                      triangleClassName={STYLES.TOOL_TIP_TRIANGLE}
                      triangleClass={STYLES.TOOL_TIP_TRIANGLE_CLASS}
                    >
                      <Heading title={t('ACH_OUT_REVIEW.TRANSFER.FEE_TOOL_TIP')} className={STYLES.TOOL_TIP_HEAD} />
                      {achFeesResponse && <p className={STYLES.TOOL_TIP_DESC}>{achFeesResponse.feeDescription}</p>}
                    </TooltipWithTriangle>
                  )}
                  <IconToolTipQuestion data-testid='ACH_OUT_REVIEW_FEE_TOOLTIP_ICON' className={STYLES.QUESTION_ICON} />
                </div>
                <Heading id='transferFee' title={`${transferFee}`} className={STYLES.TRANSFER_FEE} />
              </div>
            </div>
          </ReviewItemWrapper>
        </div>
      </div>
      <div className='w-full flex items-center justify-center py-[24px] border-t border-bw03 fadeIn'>
        <Button
          onClick={onSubmit}
          isLoading={isLoading}
          disable={isLoading || !chosenAchFee?.achTransferType}
          title={t('ACH_OUT_REVIEW.TRANSFER.CTA')}
          className={`${transferType ? 'bg-action03 hover:bg-action04 active:bg-action04' : 'bg-bw05'} !max-w-[100%] w-[375px] mt-auto text-md text-bw00 h-12 rounded font-metricRMedium`}
          id='elBtnConfirmTransfer'
        />
      </div>
    </div>
  );
};

export default ReviewAchOut;
